import { conversationQueryKey } from "@apis:conversation/conversation";
import { Message, messageQueryKey } from "@apis:message/message";
import { useMutationSearch } from "@apis:search/search";
import { SVGInternalIcon } from "@components:SVGInternalIcon";
import { useBotSelection } from "@hooks:useBotSelection";
import { useCheckBotStreaming } from "@hooks:useCheckBotStreaming";
import { useInvalidateQueries } from "@hooks:useInvalidateQueries";
import { useStreamAI } from "@hooks:useStreamAI";
import { Box, TextareaAutosize as BaseTextareaAutosize } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import { styled } from "@mui/material/styles";
import {
  useMessageToConversation,
  usePartialUpdateLastMessageByConversationId,
} from "@pages:app/talk/Chat/hooks";
import { snakeToCamelCase } from "@utils:object";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";

const Textarea = styled(BaseTextareaAutosize)(
  () => `
    flex: 1;
    resize: none;
    box-sizing: border-box;
    font-family: 'Plus Jakarta Sans Variable', sans-serif;
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
    padding: 12px 20px;
    border-radius: 30px;
    background: #EEEDF3;
    border: 0;

    &:focus {
      outline: 0;
    }

    // firefox
    &:focus-visible {
      outline: 0;
    }
  `,
);

export const ChatBox = () => {
  const { conversationId } = useParams();
  const { t } = useTranslation();
  const ERROR_MESSAGE = t("chat.error");
  const navigate = useNavigate();
  const [tryAgain, setTryAgain] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const invalidateQueries = useInvalidateQueries();

  const { selectedBotId } = useBotSelection();
  const { isStreamingEnabled, isLoading: isCheckingBotStreamingLoading } =
    useCheckBotStreaming({ botId: selectedBotId });

  useEffect(() => {
    setTryAgain(false);
  }, [selectedBotId]);

  // Add keyboard shortcut listener for ⌘+K (Mac) or Ctrl+K (Windows)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && ["k"].includes(event.key)) {
        event.preventDefault();
        textareaRef.current?.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const partialUpdateLastMessageOfConversation =
    usePartialUpdateLastMessageByConversationId(conversationId!);

  const methods = useForm<{ message: string }>({
    defaultValues: { message: "" },
  });

  const addMessageToConversation = useMessageToConversation();

  // Get the register function and destructure the ref
  const { ref: formRef, ...registerProps } = methods.register("message", {
    required: true,
  });

  const setAnswer = (f: (prev: string) => string) => {
    partialUpdateLastMessageOfConversation((oldMessage) => {
      return { ...oldMessage, answer: f(oldMessage.answer || "") };
    });
  };

  const setReferences = (data: Message["GET_ONE"]["Response"]["docs"]) => {
    partialUpdateLastMessageOfConversation((oldMessage) => {
      return {
        ...oldMessage,
        docs: snakeToCamelCase(data),
      };
    });
  };

  const askAIMutationNonStreaming = useMutationSearch({
    onSuccess: async (data) => {
      setTryAgain(false);
      if (conversationId === "new") {
        await invalidateQueries.run([
          {
            queryKey: messageQueryKey.getAll.queryKey,
          },
          {
            queryKey: conversationQueryKey.getAll.queryKey,
          },
        ]);
        addMessageToConversation(data);
        navigate(`/app/chat/${data.conversationId}`);
        return;
      }
      partialUpdateLastMessageOfConversation((oldMessage) => {
        return {
          ...oldMessage,
          answer: data.answer,
          docs: snakeToCamelCase(data.docs),
        };
      });
    },
    onError: async () => {
      setTryAgain(true);
      partialUpdateLastMessageOfConversation((oldMessage) => {
        return {
          ...oldMessage,
          answer: ERROR_MESSAGE,
        };
      });
    },
  });

  const { askAIStream } = useStreamAI({
    setReferences: setReferences,
    setAnswer,
    setMessage: async (msg) => {
      setTryAgain(false);
      if (conversationId === "new") {
        await invalidateQueries.run([
          {
            queryKey: messageQueryKey.getAll.queryKey,
          },
          {
            queryKey: conversationQueryKey.getAll.queryKey,
          },
        ]);
        addMessageToConversation(msg);
        navigate(`/app/chat/${msg.conversationId}`);
      } else {
        partialUpdateLastMessageOfConversation((oldMessage) => {
          return { ...oldMessage, id: msg.id };
        });
      }
    },
    onNoResponse: () => {
      setTryAgain(true);
      partialUpdateLastMessageOfConversation((oldMessage) => {
        return {
          ...oldMessage,
          answer: ERROR_MESSAGE,
        };
      });
    },
  });

  const submitMessage = async (data?: { message: string }) => {
    if (!data) {
      return console.warn("No data provided to submitMessage");
    }
    const question = data.message;
    methods.reset();

    // make conversation in chat area before calling API to make UX better
    const draftMessage: Partial<Message["GET_ONE"]["Response"]> = {
      question,
      botId: selectedBotId as string,
      conversationId,
      id: "new",
      answer: "",
      docs: [],
      createdAt: new Date().toISOString(),
    };

    if (tryAgain) {
      partialUpdateLastMessageOfConversation((oldMessage) => {
        return { ...oldMessage, ...draftMessage };
      });
    } else {
      addMessageToConversation(draftMessage);
    }

    if (isStreamingEnabled) {
      await askAIStream({
        question,
        botId: selectedBotId as string,
        previousConversationId: conversationId,
      });
    } else {
      const payload: {
        question: string;
        botId: string;
        conversation_id?: string;
      } = {
        question,
        botId: selectedBotId as string,
      };

      if (conversationId && !conversationId.startsWith("new")) {
        payload.conversation_id = conversationId;
      }

      await askAIMutationNonStreaming.mutateAsync(payload);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        width: "60%",
      }}
    >
      <Textarea
        placeholder={t("chat.askPlaceholder")}
        {...registerProps}
        ref={(e: HTMLTextAreaElement | null) => {
          formRef(e);
          textareaRef.current = e;
        }}
        onKeyDown={(e: any) => {
          if (e.key === "Enter") {
            e.preventDefault();
            methods.handleSubmit((data) => submitMessage(data))();
          }
        }}
      />
      <IconButton
        size="large"
        onClick={methods.handleSubmit((data) => submitMessage(data))}
        sx={{
          backgroundColor: "#EEEDF3",
          borderRadius: "50%",
          marginLeft: "1rem",
          "&:hover": {
            backgroundColor: "#E0E0E0",
          },
        }}
        disabled={
          !methods.formState.isValid ||
          !selectedBotId ||
          isCheckingBotStreamingLoading
        }
      >
        <SVGInternalIcon
          icon="sendMessage"
          sx={{
            opacity: !methods.formState.isValid || !selectedBotId ? 0.3 : 1,
          }}
        />
      </IconButton>
    </Box>
  );
};
